/**
 * The choices the API accepts (`JobSettingsIn` in server/idserver/schemas.py), laid out like sections 2, 4 and 5 of
 * the desktop app's process tab. Nothing here that the render machine cannot do (no manual framing, no matting choice,
 * no generative AI).
 */
import { useId, useState } from "react";
import type { Catalog, JobSettings, Slider, Spec } from "../api";
import {
  BEAUTY_LABELS, GEOMETRY_ALERT, GEOMETRY_NOTES, LIGHT_ADJUST_LABELS, LIGHT_FILTER_LABELS, LIGHT_MODE_LABELS, SKIN_MODE_LABELS, label,
} from "../messages";
import { BeautyRow } from "./BeautyRow";
import { ColourSwatches, fromHex, toHex, type Rgb } from "./ColourSwatches";

export { fromHex, toHex };

interface Props {
  catalog: Catalog;
  value: JobSettings;
  onChange: (v: JobSettings) => void;
}

/** Strength an item starts at when switched on: the desktop app's defaults (app/profile_store.py). */
export const BEAUTY_DEFAULTS: Record<string, number> = {
  blemish: 0.5, even_tone: 0.4, under_eye: 0.5, wrinkle: 0.4, shine: 0.5, eye_brighten: 0.4, teeth: 0.5, flyaway: 0.6,
  blush: 0.5, brow: 0.5, lipstick: 0.5, hair_colour: 0.7, eye_enlarge: 0.5,
  face_slim: 0.5, feature_balance: 0.5, nose_slim: 0.5, mouth_small: 0.5, brow_lift: 0.5, lip_full: 0.5,
};

const DEFAULT_CUSTOM: Rgb = [255, 255, 255]; // desktop profile default

/** Backgrounds a spec allows, in catalogue order (catalog `class` matches spec.backgrounds). */
export function allowedBackgrounds(catalog: Catalog, spec: Spec | undefined): string[] {
  if (!spec) return [];
  return catalog.backgrounds.filter((b) => b.class && spec.backgrounds.includes(b.class)).map((b) => b.key);
}

/** The first of `keys` (default: every background the spec allows) in the spec's own order of preference
 * (standards.py lists it: CCCD blue first, UK light grey first), else the first of `keys`. */
export function preferredBackground(catalog: Catalog, spec: Spec | undefined, keys = allowedBackgrounds(catalog, spec)):
  string | undefined {
  for (const cls of spec?.backgrounds ?? []) {
    const hit = keys.find((k) => catalog.backgrounds.find((b) => b.key === k)?.class === cls);
    if (hit) return hit;
  }
  return keys[0];
}

export function defaultSettings(catalog: Catalog, specKey = "vn_passport_4x6"): JobSettings {
  const spec = catalog.specs.find((s) => s.key === specKey) ?? catalog.specs[0];
  return {
    spec: spec.key, background: preferredBackground(catalog, spec) ?? "white", custom_rgb: null,
    light_mode: "auto", light_filter: "none", skin_mode: "light", beauty: {}, enhance: false,
  };
}

/** GĐ4: at most 4 specs from one render (server schemas.MAX_EXTRA_SPECS = 3). */
export const MAX_EXTRA_SPECS = 3;

/** The chosen specs: the main one, then the extra ones. */
export function chosenSpecs(catalog: Catalog, value: JobSettings): Spec[] {
  return [value.spec, ...(value.extra_specs ?? [])].map((k) => catalog.specs.find((s) => s.key === k))
    .filter((s): s is Spec => Boolean(s));
}

/** Backgrounds right for every chosen spec. */
export function allowedForAll(catalog: Catalog, specs: Spec[]): string[] {
  return catalog.backgrounds.filter((b) => b.class && specs.length > 0 && specs.every((s) => s.backgrounds.includes(b.class!)))
    .map((b) => b.key);
}

/** "2. Quy cách & nền" (GĐ4: + more specs from the same render, the ✓ for the colours right for all of them). */
export function SpecFields({ catalog, value, onChange }: Props) {
  const ids = { spec: useId(), add: useId() };
  const spec = catalog.specs.find((s) => s.key === value.spec);
  const extras = value.extra_specs ?? [];
  const specs = chosenSpecs(catalog, value);
  const allowed = specs.length > 1 ? allowedForAll(catalog, specs) : allowedBackgrounds(catalog, spec);
  const set = (patch: Partial<JobSettings>) => onChange({ ...value, ...patch });
  const setExtras = (next: string[]) => {
    const out = { ...value };
    if (next.length) out.extra_specs = next; else delete out.extra_specs;        // one spec: sent as before
    onChange(out);
  };
  const [custom, setCustom] = useState<Rgb>(value.custom_rgb ?? DEFAULT_CUSTOM);
  const allowedLabels = catalog.backgrounds.filter((b) => allowedBackgrounds(catalog, spec).includes(b.key)).map((b) => b.label);
  const bg = catalog.backgrounds.find((b) => b.key === value.background);
  const wrongFor = bg?.class ? specs.filter((s) => !s.backgrounds.includes(bg.class!)) : [];
  const label = (key: string) => catalog.specs.find((s) => s.key === key)?.label ?? key;

  return (
    <div className="options">
      <div className="field">
        <label htmlFor={ids.spec}>Quy cách</label>
        <select id={ids.spec} value={value.spec}
                onChange={(e) => {
                  const next = catalog.specs.find((s) => s.key === e.target.value);
                  const rest = extras.filter((k) => k !== e.target.value);
                  const pick = preferredBackground(catalog, next, allowedForAll(catalog, [next, ...rest.map((k) =>
                    catalog.specs.find((s) => s.key === k))].filter((s): s is Spec => Boolean(s))));
                  const out: JobSettings = { ...value, spec: e.target.value, custom_rgb: null,
                                             background: pick ?? preferredBackground(catalog, next) ?? value.background };
                  if (rest.length) out.extra_specs = rest; else delete out.extra_specs;
                  onChange(out);
                }}>
          {catalog.specs.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        {spec && (
          <small className="hint">
            {spec.width_mm} × {spec.height_mm} mm, {spec.dpi} dpi
            {allowedLabels.length > 0 && ` · nền đúng quy cách: ${allowedLabels.join(", ")}`}
          </small>
        )}
      </div>

      <div className="field extra-specs">
        <label htmlFor={ids.add}>Thêm quy cách (cùng ảnh, mỗi quy cách +1 token)</label>
        {extras.length > 0 && (
          <ul className="spec-chips">
            {extras.map((k) => {
              const s = catalog.specs.find((x) => x.key === k);
              return (
                <li key={k} className="chip">
                  <span>{label(k)}{s ? ` · ${s.width_mm} × ${s.height_mm} mm` : ""}</span>
                  <button type="button" className="chip-remove" aria-label={`Bỏ quy cách ${label(k)}`}
                          onClick={() => setExtras(extras.filter((x) => x !== k))}>✕</button>
                </li>
              );
            })}
          </ul>
        )}
        {extras.length < MAX_EXTRA_SPECS ? (
          <select id={ids.add} value="" onChange={(e) => { if (e.target.value) setExtras([...extras, e.target.value]); }}>
            <option value="">+ Thêm quy cách…</option>
            {catalog.specs.filter((s) => s.key !== value.spec && !extras.includes(s.key))
              .map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        ) : <small className="hint">Tối đa {MAX_EXTRA_SPECS + 1} quy cách mỗi lần render.</small>}
        {extras.length > 0 && (
          <small className="hint">Mỗi quy cách được căn khung, kiểm tra chuẩn và tải riêng; tờ in xếp được nhiều quy cách.</small>
        )}
      </div>

      <ColourSwatches legend="Màu nền" swatches={catalog.backgrounds} value={value.background} custom={custom}
                      marked={(key) => allowed.includes(key)}
                      hint={specs.length > 1 ? "Dấu ✓ là màu nền đúng mọi quy cách đã chọn." : "Dấu ✓ là màu nền đúng quy cách đã chọn."}
                      onSelect={(key) => set({ background: key, custom_rgb: key === "custom" ? custom : null })}
                      onCustom={(rgb) => { setCustom(rgb); set({ background: "custom", custom_rgb: rgb }); }} />
      {specs.length > 1 && wrongFor.length > 0 && (
        <p className="warn" role="note">Màu nền này chưa đúng quy cách: {wrongFor.map((s) => s.label).join(", ")}.</p>
      )}
      <p className="hint">Khung ảnh tự căn theo khuôn mặt (đỉnh đầu, mắt, cằm) đúng quy cách đã chọn.</p>
    </div>
  );
}

/** "+0.4 EV" for exposure, else the share of the slider's range: "+50%" / "30%". */
function sliderText(s: Pick<Slider, "key" | "min" | "max">, v: number): string {
  if (s.key === "exposure_ev") return `${v > 0 ? "+" : ""}${v.toFixed(1)} EV`;
  const pct = Math.round((v / (s.max || 1)) * 100);
  return s.min < 0 && pct > 0 ? `+${pct}%` : `${pct}%`;
}

/** "3. Ánh sáng & da". */
export function LightFields({ catalog, value, onChange }: Props) {
  const ids = { light: useId(), filter: useId(), skin: useId(), enhance: useId(), enhanceHint: useId(), adjust: useId() };
  const set = (patch: Partial<JobSettings>) => onChange({ ...value, ...patch });
  const sliders = catalog.light_adjust ?? [];
  const skinRange = catalog.skin_strength ?? { min: 0, max: 1, step: 0.05, default: 0.25 };
  // kept while another mode is chosen, so switching back restores the sliders
  const [adjust, setAdjust] = useState<Record<string, number>>(value.light_adjust ?? {});
  const [skin, setSkin] = useState(value.skin_strength ?? skinRange.default);

  function chooseLight(mode: string) {
    const patch: Partial<JobSettings> = { light_mode: mode, light_adjust: mode === "auto_plus" ? adjust : undefined };
    set(patch);
  }

  function slide(key: string, v: number) {
    const next = { ...adjust, [key]: v };
    if (v === 0) delete next[key];                     // only what moves away from the automatic result is sent
    setAdjust(next);
    set({ light_adjust: next });
  }

  function chooseSkin(mode: string) {
    set({ skin_mode: mode, skin_strength: mode === "custom" ? skin : undefined });
  }

  return (
    <div className="options">
      <div className="field-row">
        <div className="field">
          <label htmlFor={ids.light}>Ánh sáng</label>
          <select id={ids.light} value={value.light_mode} onChange={(e) => chooseLight(e.target.value)}>
            {catalog.light_modes.map((m) => <option key={m} value={m}>{label(LIGHT_MODE_LABELS, m)}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor={ids.filter}>Bộ lọc</label>
          <select id={ids.filter} value={value.light_filter} onChange={(e) => set({ light_filter: e.target.value })}>
            {catalog.light_filters.map((m) => <option key={m} value={m}>{label(LIGHT_FILTER_LABELS, m)}</option>)}
          </select>
        </div>
      </div>
      {value.light_mode === "auto_plus" && sliders.length > 0 && (
        <fieldset className="field adjust" aria-describedby={ids.adjust}>
          <legend>Tinh chỉnh trên ánh sáng tự động</legend>
          <small id={ids.adjust} className="hint">0 = giữ như tự động. Mỗi lần đổi cần render lại để xem kết quả.</small>
          {sliders.map((s) => {
            const v = adjust[s.key] ?? s.default;
            return (
              <label key={s.key} className="adjust-row">
                <span>{label(LIGHT_ADJUST_LABELS, s.key)}</span>
                <input type="range" min={s.min} max={s.max} step={s.step} value={v}
                       aria-label={label(LIGHT_ADJUST_LABELS, s.key)} aria-valuetext={sliderText(s, v)}
                       onChange={(e) => slide(s.key, Number(e.target.value))} />
                <output>{sliderText(s, v)}</output>
              </label>
            );
          })}
          <div className="actions actions-end">
            <button type="button" className="btn btn-small" disabled={Object.keys(adjust).length === 0}
                    onClick={() => { setAdjust({}); set({ light_adjust: {} }); }}>Đặt lại</button>
          </div>
        </fieldset>
      )}
      <div className="field">
        <label htmlFor={ids.skin}>Làm mịn da</label>
        <select id={ids.skin} value={value.skin_mode} onChange={(e) => chooseSkin(e.target.value)}>
          {catalog.skin_modes.map((m) => <option key={m} value={m}>{label(SKIN_MODE_LABELS, m)}</option>)}
        </select>
      </div>
      {value.skin_mode === "custom" && (
        <label className="adjust-row">
          <span>Độ mịn</span>
          <input type="range" min={skinRange.min} max={skinRange.max} step={skinRange.step} value={skin}
                 aria-label="Độ mịn" aria-valuetext={`${Math.round(skin * 100)} %`}
                 onChange={(e) => { const v = Number(e.target.value); setSkin(v); set({ skin_strength: v }); }} />
          <output>{Math.round(skin * 100)}%</output>
        </label>
      )}
      <div className="check">
        <input id={ids.enhance} type="checkbox" checked={value.enhance} aria-describedby={ids.enhanceHint}
               onChange={(e) => set({ enhance: e.target.checked })} />
        <label htmlFor={ids.enhance}>Tăng nét AI</label>
      </div>
      <small id={ids.enhanceHint} className="hint">Làm rõ chi tiết ảnh gốc nhỏ hoặc hơi mờ; giữ nguyên khuôn mặt.</small>
    </div>
  );
}

/** "4. Làm đẹp (chỉ bật khi cần)": per item an on/off switch and a remembered strength, everything off by default. */
export function BeautyFields({ catalog, value, onChange }: Props) {
  const [strength, setStrength] = useState<Record<string, number>>(() => ({ ...BEAUTY_DEFAULTS, ...value.beauty }));
  const geometryOn = catalog.geometry_items.some((k) => (value.beauty[k] ?? 0) > 0);
  const setBeauty = (beauty: Record<string, number>) => onChange({ ...value, beauty });

  function toggle(item: string, on: boolean) {
    const beauty = { ...value.beauty };
    if (on) beauty[item] = strength[item] ?? 0.5; else delete beauty[item];
    setBeauty(beauty);
  }

  function slide(item: string, n: number) {
    setStrength((s) => ({ ...s, [item]: n }));
    if (item in value.beauty) setBeauty({ ...value.beauty, [item]: n });
  }

  /** Lipstick / hair target colour ("Màu…" of the desktop app); picking one also switches the item on. */
  function pickColour(item: string, hex: string) {
    const rgb = fromHex(hex);
    if (!rgb) return;
    const beauty = item in value.beauty ? value.beauty : { ...value.beauty, [item]: strength[item] ?? 0.5 };
    onChange({ ...value, beauty, beauty_rgb: { ...(value.beauty_rgb ?? {}), [item]: rgb } });
  }

  return (
    <div className="beauty">
      <ul className="beauty-list">
        {catalog.beauty_items.map((item) => {
          const name = label(BEAUTY_LABELS, item);
          const colour = catalog.beauty_colours?.[item];
          return (
            <li key={item}>
              <BeautyRow label={name} value={strength[item] ?? 0.5} min={0.05} on={(value.beauty[item] ?? 0) > 0}
                         onToggle={(on) => toggle(item, on)} sliderLabel={`Độ mạnh: ${name}`}
                         onChange={(n) => slide(item, n)}
                         note={catalog.geometry_items.includes(item)
                           ? `${GEOMETRY_NOTES[item] ?? "Đổi hình dạng khuôn mặt."} Chỉ cho ảnh kỷ niệm, hồ sơ nội bộ.` : undefined}
                         colour={colour ? { value: toHex(value.beauty_rgb?.[item] ?? colour), label: `Màu: ${name}`,
                                            onPick: (hex) => pickColour(item, hex) } : undefined} />
            </li>
          );
        })}
      </ul>
      {geometryOn && <p role="alert" className="warn">{GEOMETRY_ALERT}</p>}
      <div className="actions actions-end">
        <button type="button" className="btn btn-small" disabled={Object.keys(value.beauty).length === 0}
                onClick={() => setBeauty({})}>Tắt hết làm đẹp</button>
      </div>
    </div>
  );
}
